import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Cookie categories for GDPR compliance
export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

// Cookie consent preferences
export interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

// Cookie consent state
export interface CookieConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
  consentDate: string | null;
  showBanner: boolean;
}

// Cookie context interface
interface CookieConsentContextType {
  state: CookieConsentState;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (preferences: Partial<CookiePreferences>) => void;
  openSettings: () => void;
  hideBanner: () => void;
  resetConsent: () => void;
  isConsentRequired: () => boolean;
}

// Default preferences (essential cookies are always required)
const defaultPreferences: CookiePreferences = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

// Initial state
const initialState: CookieConsentState = {
  hasConsented: false,
  preferences: defaultPreferences,
  consentDate: null,
  showBanner: true,
};

// Cookie consent context
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

// Local storage key for cookie preferences
const COOKIE_CONSENT_KEY = 'vaiu_cookie_consent';

// Cookie consent provider component
export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CookieConsentState>(initialState);

  // Load saved preferences on mount
  useEffect(() => {
    const loadSavedPreferences = () => {
      try {
        const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          setState(prev => ({
            ...prev,
            hasConsented: true,
            preferences: { ...defaultPreferences, ...parsed.preferences },
            consentDate: parsed.consentDate,
            showBanner: false,
          }));
        } else {
          // Show banner if no consent has been given
          setState(prev => ({ ...prev, showBanner: true }));
        }
      } catch (error) {
        console.error('Error loading cookie preferences:', error);
        setState(prev => ({ ...prev, showBanner: true }));
      }
    };

    loadSavedPreferences();
  }, []);

  // Save preferences to localStorage
  const savePreferences = (preferences: CookiePreferences) => {
    const consentData = {
      preferences,
      consentDate: new Date().toISOString(),
      version: '1.0', // For future compatibility
    };

    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  };

  // Set cookies based on preferences
  const applyCookiePreferences = (preferences: CookiePreferences) => {
    // Handle analytics cookies (Google Analytics, etc.)
    if (preferences.analytics) {
      // Enable analytics tracking
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    } else {
      // Disable analytics tracking
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    }

    // Handle marketing cookies
    if (preferences.marketing) {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
    } else {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    }

    // Handle functional cookies
    if (!preferences.functional) {
      // Remove non-essential functional cookies
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        // Remove specific functional cookies (customize based on your needs)
        if (name.startsWith('_pref_') || name.startsWith('_settings_')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      });
    }
  };

  // Accept all cookies
  const acceptAll = () => {
    const preferences: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };

    setState(prev => ({
      ...prev,
      hasConsented: true,
      preferences,
      consentDate: new Date().toISOString(),
      showBanner: false,
    }));

    savePreferences(preferences);
    applyCookiePreferences(preferences);
  };

  // Reject all non-essential cookies
  const rejectAll = () => {
    const preferences: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };

    setState(prev => ({
      ...prev,
      hasConsented: true,
      preferences,
      consentDate: new Date().toISOString(),
      showBanner: false,
    }));

    savePreferences(preferences);
    applyCookiePreferences(preferences);
  };

  // Accept selected preferences
  const acceptSelected = (selectedPreferences: Partial<CookiePreferences>) => {
    const preferences: CookiePreferences = {
      essential: true, // Always true
      functional: selectedPreferences.functional ?? false,
      analytics: selectedPreferences.analytics ?? false,
      marketing: selectedPreferences.marketing ?? false,
    };

    setState(prev => ({
      ...prev,
      hasConsented: true,
      preferences,
      consentDate: new Date().toISOString(),
      showBanner: false,
    }));

    savePreferences(preferences);
    applyCookiePreferences(preferences);
  };

  // Open cookie settings
  const openSettings = () => {
    setState(prev => ({ ...prev, showBanner: true }));
  };

  // Hide banner without consent
  const hideBanner = () => {
    setState(prev => ({ ...prev, showBanner: false }));
  };

  // Reset consent (for testing or user request)
  const resetConsent = () => {
    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      setState(initialState);
      
      // Clear all non-essential cookies
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (!name.startsWith('auth') && !name.startsWith('session')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      });

      // Reset Google consent
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'default', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    } catch (error) {
      console.error('Error resetting cookie consent:', error);
    }
  };

  // Check if consent is required (for EU users or based on location)
  const isConsentRequired = () => {
    // You can implement geolocation detection here
    // For now, we'll require consent for all users to be safe
    return true;
  };

  const contextValue: CookieConsentContextType = {
    state,
    acceptAll,
    rejectAll,
    acceptSelected,
    openSettings,
    hideBanner,
    resetConsent,
    isConsentRequired,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Hook to use cookie consent context
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Cookie utility functions
export const cookieUtils = {
  // Set a cookie with proper consent checking
  setCookie: (name: string, value: string, category: CookieCategory, days = 30) => {
    const context = useContext(CookieConsentContext);
    if (!context) return false;

    // Always allow essential cookies
    if (category === 'essential' || context.state.preferences[category]) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
      return true;
    }
    return false;
  },

  // Get a cookie value
  getCookie: (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },

  // Delete a cookie
  deleteCookie: (name: string) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
  },
};

// TypeScript declaration for Google Analytics
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
} 